/**
 * W-031 — User detail. Stories: US-A06, US-805, US-A07.
 *
 * AC2 profile, stream, city, registration date, subscription status, progress
 * AC3 activity: last login, total watch time. The downloads stat is
 * deferred with the offline package (docs/01-PRD.md §12.1, DR-001).
 * AC5 deactivation with a recorded reason
 *
 * The admin actions available here are role-gated: only Support and the Super
 * Admin see grant/revoke, only a Moderator and the Super Admin see ban. RLS
 * refuses the rest regardless of what the UI shows (docs/03 §10).
 */
import { PageHeader } from '@refinedev/antd'
import { CanAccess, useInvalidate, useList, useShow } from '@refinedev/core'
import { Button, Card, Col, Descriptions, Row, Space, Spin, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ProfileAvatar, StatTile, StatusTag } from '@/components'
import { BanUserDialog, DeactivateUserDialog, RevokeSubscriptionDialog } from '@/components/dialogs'
import { invokeEdgeFunction } from '@/lib/supabase'
import { formatPrice } from '@horizon/i18n'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { City, Profile, Subscription, Transaction } from '@/types'
import { GrantSubscriptionModal } from './GrantSubscriptionModal'
import { useStreams } from '../subjects/useStreams'

interface ProfileDetail extends Profile {
  email?: string | null
  last_sign_in_at?: string | null
  /** Aggregates from the admin view — see the TODO in list.tsx. */
  total_watched_sec?: number | null
  lessons_completed?: number | null
}

export const UserShowPage = () => {
  const translate = useAdminTranslate()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // `profiles_admin` is a data-only view, not the `users` route resource, so
  // Refine cannot infer the route ID once the resource is overridden.
  const { query } = useShow<ProfileDetail>({ resource: 'profiles_admin', id })
  const user = query.data?.data

  if (!user) {
    return (
      <PageHeader title={translate('admin.pages.user')} onBack={() => navigate('/users')}>
        <Spin spinning />
      </PageHeader>
    )
  }

  // The subscription and transaction queries are filtered by the profile id, so
  // they must not run before it is known. Mounting them in a child component
  // that only exists once the profile has loaded expresses that in the tree,
  // instead of leaving an unfiltered query switched off by a flag.
  return <UserShowContent user={user} onBack={() => navigate('/users')} />
}

const UserShowContent = ({ user, onBack }: { user: ProfileDetail; onBack: () => void }) => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const invalidate = useInvalidate()

  const { streams } = useStreams()
  const { data: citiesData } = useList<City>({ resource: 'cities', pagination: { mode: 'off' } })
  const cities = citiesData?.data ?? []

  const { data: subscriptionsData } = useList<Subscription>({
    resource: 'subscriptions',
    filters: [{ field: 'user_id', operator: 'eq', value: user.id }],
    sorters: [{ field: 'created_at', order: 'desc' }],
  })
  const subscriptions = subscriptionsData?.data ?? []
  const active = subscriptions.find(subscription => subscription.status === 'active') ?? null

  const { data: transactionsData } = useList<Transaction>({
    resource: 'transactions',
    filters: [{ field: 'user_id', operator: 'eq', value: user.id }],
    sorters: [{ field: 'created_at', order: 'desc' }],
  })

  const [grantOpen, setGrantOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [banOpen, setBanOpen] = useState(false)
  const [deactivateOpen, setDeactivateOpen] = useState(false)

  const refresh = () => {
    void invalidate({ resource: 'profiles_admin', invalidates: ['detail'], id: user.id })
    void invalidate({ resource: 'subscriptions', invalidates: ['list'] })
  }

  return (
    <PageHeader
      title={
        <Space>
          <ProfileAvatar name={user.full_name} url={user.avatar_url} size={40} />
          {user.full_name}
        </Space>
      }
      onBack={onBack}
      extra={
        <Space wrap>
          <CanAccess resource="users" action="grant">
            <Button type="primary" onClick={() => setGrantOpen(true)}>
              {translate('admin.screens.userShow.grant')}
            </Button>
          </CanAccess>
          <CanAccess resource="users" action="revoke">
            <Button danger disabled={!active} onClick={() => setRevokeOpen(true)}>
              {translate('admin.screens.userShow.revoke')}
            </Button>
          </CanAccess>
          <CanAccess resource="users" action="ban">
            <Button danger onClick={() => setBanOpen(true)}>
              {user.is_banned
                ? translate('admin.screens.userShow.editBan')
                : translate('admin.screens.userShow.ban')}
            </Button>
          </CanAccess>
          <CanAccess resource="users" action="edit">
            <Button danger={!user.deactivated_at} onClick={() => setDeactivateOpen(true)}>
              {user.deactivated_at
                ? translate('admin.screens.userShow.reactivate')
                : translate('admin.screens.userShow.deactivate')}
            </Button>
          </CanAccess>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={translate('admin.screens.userShow.profile')}>
          <Descriptions column={{ xs: 1, md: 2 }} size="small">
            <Descriptions.Item label={translate('admin.columns.name')}>
              {user.full_name}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.phone')}>
              {user.phone}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.email')}>
              {user.email ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.stream')}>
              {streams.find(stream => stream.id === user.stream_id)?.name_fr ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.city')}>
              {cities.find(city => city.id === user.city_id)?.name_fr ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.language')}>
              {user.locale}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.registeredAt')}>
              {dayjs(user.created_at).format('DD/MM/YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.lastLogin')}>
              {user.last_sign_in_at ? dayjs(user.last_sign_in_at).format('DD/MM/YYYY HH:mm') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.state')}>
              {user.deactivated_at ? (
                <Tag color="error">{translate('admin.common.deactivated')}</Tag>
              ) : user.is_banned ? (
                <Tag color="warning">
                  {user.banned_until
                    ? translate('admin.screens.userShow.bannedUntil', {
                        date: dayjs(user.banned_until).format('DD/MM/YYYY'),
                      })
                    : translate('admin.screens.userShow.bannedPermanent')}
                </Tag>
              ) : (
                <Tag color="success">{translate('admin.common.active')}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.reason')}>
              {user.ban_reason ?? '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* AC3 — activity summary. */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <StatTile
              label={translate('admin.screens.userShow.watchTime')}
              value={user.total_watched_sec ? Math.round(user.total_watched_sec / 3600) : 0}
              suffix={translate('admin.screens.userShow.hoursSuffix')}
            />
          </Col>
          <Col xs={24} md={8}>
            <StatTile
              label={translate('admin.screens.userShow.lessonsCompleted')}
              value={user.lessons_completed ?? 0}
            />
          </Col>
        </Row>

        <Card size="small" title={translate('admin.screens.userShow.subscriptions')}>
          <Table<Subscription>
            dataSource={subscriptions}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: translate('admin.columns.status'),
                dataIndex: 'status',
                render: (value: Subscription['status']) => (
                  <StatusTag kind="subscription" value={value} />
                ),
              },
              {
                title: translate('admin.columns.startsAt'),
                dataIndex: 'starts_at',
                render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
              },
              {
                title: translate('admin.columns.expiresAt'),
                dataIndex: 'expires_at',
                render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
              },
              { title: translate('admin.columns.source'), dataIndex: 'source' },
              {
                title: translate('admin.columns.reason'),
                dataIndex: 'reason',
                render: (value: string | null) => value ?? '—',
              },
            ]}
          />
        </Card>

        <Card size="small" title={translate('admin.screens.userShow.transactions')}>
          <Table<Transaction>
            dataSource={transactionsData?.data ?? []}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: translate('admin.columns.date'),
                dataIndex: 'created_at',
                render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
              },
              {
                title: translate('admin.columns.amount'),
                dataIndex: 'amount_mru',
                render: (value: number) => formatPrice(value, locale),
              },
              { title: translate('admin.columns.source'), dataIndex: 'provider' },
              {
                title: translate('admin.columns.status'),
                dataIndex: 'status',
                render: (value: Transaction['status']) => (
                  <StatusTag kind="transaction" value={value} />
                ),
              },
            ]}
          />
        </Card>

        <Typography.Text type="secondary">
          {translate('admin.screens.userShow.auditNotice')}
        </Typography.Text>
      </Space>

      {/* W-032 */}
      <GrantSubscriptionModal
        open={grantOpen}
        userId={user.id}
        userName={user.full_name}
        currentSubscription={active}
        onClose={() => setGrantOpen(false)}
        onGranted={refresh}
      />

      {/* WD-7 */}
      <RevokeSubscriptionDialog
        open={revokeOpen}
        userName={user.full_name}
        planName={active?.plan_id ?? '—'}
        expiresAt={active ? dayjs(active.expires_at).format('DD/MM/YYYY') : '—'}
        onCancel={() => setRevokeOpen(false)}
        onConfirm={async reason => {
          // T-002: revokes and writes the audit entry atomically (AC4, AC5).
          await invokeEdgeFunction('subscription-revoke', {
            subscription_id: active?.id,
            reason,
          })
          refresh()
          setRevokeOpen(false)
        }}
      />

      {/* WD-4 */}
      <BanUserDialog
        open={banOpen}
        userName={user.full_name}
        onCancel={() => setBanOpen(false)}
        onConfirm={async ({ duration, reason }) => {
          // TODO(US-A07): `moderation-ban` is the working edge-function name.
          await invokeEdgeFunction('moderation-ban', {
            user_id: user.id,
            duration,
            reason,
          })
          refresh()
          setBanOpen(false)
        }}
      />

      {/* WD-6 */}
      <DeactivateUserDialog
        open={deactivateOpen}
        userName={user.full_name}
        reactivate={Boolean(user.deactivated_at)}
        onCancel={() => setDeactivateOpen(false)}
        onConfirm={async reason => {
          // T-003: deactivation clears/sets deactivated_at, revokes live
          // sessions, and writes the audit entry atomically — no separate
          // recordAudit() call (see lib/audit.ts: actions with their own
          // edge function write their own audit row).
          await invokeEdgeFunction('user-set-active', {
            user_id: user.id,
            active: Boolean(user.deactivated_at),
            reason,
          })
          refresh()
          setDeactivateOpen(false)
        }}
      />
    </PageHeader>
  )
}
